import type Luminary from '@lumen5/luminary';
import type { TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { GetRootState } from 'js/src/libs/RootState';

import { getNonRecommendableAssetNames } from '../../video-state/serialization/assetSerializer';
import { getMediaUuidFromAsset } from '../../video-state/serialization/mediaHelpers';
import type { LintFinding } from '../videoLinter';

/**
 * Warning: the same media is on more than one scene. Repeats make the video monotonous, and on
 * consecutive scenes the media's animation visibly restarts. `set_media` warns as each repeat is
 * placed, but that note is easy to lose in a large batch of edits — this is the check that holds
 * at the end. Media the design fixes in place (non-editable or non-recommendable) is left out, as
 * is a voicedriven group's base video (talking head), which every scene in the group links to.
 */
export function checkDuplicateMedia(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const sceneIndexesByUuid = new Map<string, number[]>();

    luminary.scenes.forEach((scene, sceneIndex) => {
        const nonRecommendable = getNonRecommendableAssetNames(scene, getState());
        const sceneUuids = new Set(
            scene
                .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
                .filter(
                    (asset) =>
                        asset.isEditable &&
                        !nonRecommendable.has(asset.name) &&
                        !(asset as TimeAwareMediaAsset).linkToSceneGroup,
                )
                .map(getMediaUuidFromAsset)
                .filter((mediaUuid): mediaUuid is string => Boolean(mediaUuid)),
        );
        sceneUuids.forEach((mediaUuid) => {
            sceneIndexesByUuid.set(mediaUuid, [
                ...(sceneIndexesByUuid.get(mediaUuid) ?? []),
                sceneIndex,
            ]);
        });
    });

    const repeats = [...sceneIndexesByUuid].filter(([, sceneIndexes]) => sceneIndexes.length > 1);
    if (repeats.length === 0) {
        return [];
    }

    const registry = MediaRegistry.getInstance();
    return repeats.map(([mediaUuid, sceneIndexes]) => {
        const mediaLabel = registry.getItemIdByUuid(mediaUuid) ?? 'The same media';
        const isBackToBack = sceneIndexes.some(
            (sceneIndex, i) => i > 0 && sceneIndex === sceneIndexes[i - 1] + 1,
        );
        const backToBackNote = isBackToBack
            ? ', some of them back to back, where its animation visibly restarts'
            : '';
        return {
            check: 'duplicate_media',
            severity: 'warning',
            sceneId: sceneIndexes.map((sceneIndex) => luminary.scenes[sceneIndex].id),
            message: `${mediaLabel} is on ${sceneIndexes.length} scenes${backToBackNote}. Unless the repetition is deliberate, give each scene its own media with \`${TOOL_NAMES.setMedia}\`. If there isn't enough suitable media for every slot, switch scenes to designs with fewer media slots, or replan the scenes around the media you have.`,
        };
    });
}
